import type { Currency } from '@/shared/lib';

/** Subscription tiers, cheapest first. */
export const PLAN_TIERS = ['free', 'solo', 'pro', 'studio'] as const;

export type PlanTier = (typeof PLAN_TIERS)[number];

/** Features that a tier can gate. Keep in step with the backend enum. */
export type PlanFeature =
  | 'clientReminders'
  | 'analytics'
  | 'crmNotes'
  | 'teamMembers'
  | 'customDomain'
  | 'catalogBoost'
  | 'unlimitedPortfolio'
  | 'reviewReplies';

export type PlanDefinition = {
  tier: PlanTier;
  /** Monthly price per currency; annual billing applies ANNUAL_DISCOUNT. */
  price: Record<Currency, number>;
  /** Online bookings included per month; null means unlimited. */
  bookingsPerMonth: number | null;
  /** Portfolio photos allowed; null means unlimited. */
  portfolioPhotos: number | null;
  /** Active specialists the plan covers (Studio is the only multi-seat tier). */
  seats: number;
  features: readonly PlanFeature[];
};

/** Annual billing is charged at ten months' price. */
export const ANNUAL_DISCOUNT = 0.2;

/**
 * The pricing matrix from the market research.
 *
 * The Free tier is deliberately generous on volume (75 bookings vs. the 30 a
 * key competitor allows) and gated on function instead: automatic client
 * reminders are the upgrade driver, because a single prevented no-show pays for
 * months of Solo.
 */
export const PLANS: Record<PlanTier, PlanDefinition> = {
  free: {
    tier: 'free',
    price: { UAH: 0, EUR: 0 },
    bookingsPerMonth: 75,
    portfolioPhotos: 10,
    seats: 1,
    features: [],
  },
  solo: {
    tier: 'solo',
    price: { UAH: 149, EUR: 4.99 },
    bookingsPerMonth: 150,
    portfolioPhotos: 30,
    seats: 1,
    features: ['clientReminders', 'crmNotes', 'reviewReplies'],
  },
  pro: {
    tier: 'pro',
    price: { UAH: 299, EUR: 9.99 },
    bookingsPerMonth: null,
    portfolioPhotos: null,
    seats: 1,
    features: [
      'clientReminders',
      'crmNotes',
      'analytics',
      'catalogBoost',
      'unlimitedPortfolio',
      'reviewReplies',
    ],
  },
  studio: {
    tier: 'studio',
    price: { UAH: 699, EUR: 29.99 },
    bookingsPerMonth: null,
    portfolioPhotos: null,
    seats: 4,
    features: [
      'clientReminders',
      'crmNotes',
      'analytics',
      'catalogBoost',
      'unlimitedPortfolio',
      'reviewReplies',
      'teamMembers',
      'customDomain',
    ],
  },
};

/** True when the tier includes the feature. */
export const planHasFeature = (tier: PlanTier, feature: PlanFeature): boolean =>
  PLANS[tier].features.includes(feature);

/**
 * Monthly price when billed annually: whole hryvnia, euro cents
 * (149 → 119 UAH, 4.99 → 3.99 EUR, as in the research price sheet).
 */
export const annualMonthlyPrice = (tier: PlanTier, currency: Currency): number => {
  const factor = currency === 'UAH' ? 1 : 100;
  return Math.round(PLANS[tier].price[currency] * (1 - ANNUAL_DISCOUNT) * factor) / factor;
};
